import { LineChart } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import {
  useForecastPnl,
  type ForecastCurrency,
  type ForecastPnlRow,
} from "@/hooks/finance/cash-flow/useForecastPnl";

type Props = {
  displayCurrency: ForecastCurrency;
};

/**
 * Forecast P&L card — projects Revenue and COGS forward and derives CP1/CP2 per
 * month. Estimate, not a commitment.
 */
export function ForecastPnl({ displayCurrency }: Props) {
  const { data, isLoading } = useForecastPnl(displayCurrency);
  const fmt = (v: number) => (v ? formatCurrency(v, displayCurrency) : "–");
  const pct = (r: number) => (Number.isFinite(r) ? `${(r * 100).toFixed(1)}%` : "–");

  const basisLabel = data?.basisMonths.length
    ? `${data.basisMonths[0].label} – ${data.basisMonths[data.basisMonths.length - 1].label}`
    : "";

  const renderCell = (row: ForecastPnlRow, i: number, base: string) => {
    if (row.kind === "header") return <td key={i} className={base} />;
    if (row.kind === "margin")
      return (
        <td key={i} className={cn(base, "text-right tabular-nums text-emerald-700 font-semibold")}>
          {pct(row.values[i])}
        </td>
      );
    return (
      <td key={i} className={cn(base, "text-right tabular-nums")}>
        {fmt(row.values[i])}
      </td>
    );
  };

  return (
    <Card className="border-none shadow-lg overflow-hidden bg-white">
      <CardHeader className="bg-slate-900 border-none pb-4">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <LineChart className="h-4 w-4 text-emerald-400" />
              Forecast P&amp;L — Company
            </CardTitle>
            <CardDescription className="text-slate-400 text-[10px]">
              Company-wide revenue &amp; operating costs projected from recent trends
              → Gross Profit and Operating Profit. An estimate.
            </CardDescription>
          </div>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[9px] uppercase font-black tracking-widest">
            Estimate
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-24 bg-slate-50/20">
            <div className="h-9 w-9 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : !data ? (
          <div className="py-16 text-center text-sm text-slate-400">
            Not enough recent history to build a forecast.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 z-10 bg-slate-50 w-72 px-4 py-3 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-slate-200">
                    Line Item
                  </th>
                  {data.forecastMonths.map((m) => (
                    <th
                      key={m.key}
                      className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[120px]"
                    >
                      {m.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[130px] border-l border-slate-200">
                    3-mo Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((row) => {
                  if (row.kind === "header") {
                    return (
                      <tr key={row.key} className="bg-slate-100/70 border-y border-slate-200">
                        <td className="sticky left-0 z-10 bg-slate-100/70 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 border-r border-slate-200">
                          {row.label}
                        </td>
                        {data.forecastMonths.map((m) => (
                          <td key={m.key} className="px-4 py-1.5" />
                        ))}
                        <td className="px-4 py-1.5 border-l border-slate-200" />
                      </tr>
                    );
                  }
                  const isCp = row.kind === "cp";
                  const isTotal = row.kind === "total";
                  const rowCls = cn(
                    "hover:bg-slate-50/50",
                    isCp && "bg-emerald-50/60 font-bold",
                    isTotal && "bg-slate-50 font-semibold",
                    row.kind === "margin" && "italic",
                    (row.kind === "sub" || row.kind === "cost") && "text-slate-700"
                  );
                  const labelCls = cn(
                    "sticky left-0 z-10 px-4 py-2 text-[13px] border-r border-slate-100",
                    isCp ? "bg-emerald-50/60 text-slate-900" : isTotal ? "bg-slate-50 text-slate-900 font-semibold" : "bg-white",
                    row.kind === "margin" && "pl-8 text-[11px] text-emerald-700",
                    (row.kind === "sub") && "pl-8 text-muted-foreground",
                    (row.kind === "cost") && "pl-6 text-slate-600"
                  );
                  const cellBase = "px-4 py-2 text-[13px] border-slate-100";
                  const totalBase = cn(
                    cellBase,
                    "border-l border-slate-200 text-right tabular-nums font-semibold",
                    isCp ? "text-slate-900" : isTotal ? "text-slate-900" : "text-slate-700",
                    row.kind === "margin" && "text-emerald-700 font-semibold italic"
                  );
                  return (
                    <tr key={row.key} className={rowCls}>
                      <td className={labelCls}>{row.label}</td>
                      {data.forecastMonths.map((_, i) => renderCell(row, i, cellBase))}
                      <td className={totalBase}>
                        {row.total == null
                          ? "–"
                          : row.kind === "margin"
                          ? pct(row.total)
                          : fmt(row.total)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <p className="px-4 py-2.5 text-[10px] text-slate-400 border-t border-slate-100">
              Revenue &amp; recurring costs extend their trend over {basisLabel};
              periodic costs (e.g. quarterly/annual taxes) are amortized evenly
              over 12 months. Gross Profit = Revenue − Purchases − variable costs;
              Operating Profit = Gross Profit − SG&amp;A. Operating activities only.
              Estimate, not a commitment.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
